import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import {
  ArrowRight, ArrowLeft, CheckCircle2, ExternalLink, Copy,
  ShieldCheck, KeyRound, MousePointerClick, Mail, X,
} from "lucide-react";

/**
 * Inline tutorial for generating a Google App Password.
 *
 * Many users hit ``AUTHENTICATIONFAILED`` when wiring their Gmail to our
 * IMAP poller because Gmail no longer accepts the regular account password
 * (deprecated since May 2022). The fix is straightforward — generate an
 * App Password — but the UI flow on the Google side is buried 3 menus deep.
 *
 * This component walks the user through the 3 concrete steps in 60 seconds:
 *   1) Enable 2-Step Verification (prerequisite).
 *   2) Generate the App Password.
 *   3) Copy & paste the 16-char password back into the form.
 *
 * The illustrations are CSS-only mockups of the Google panels (no external
 * image assets needed — they always render even on slow networks).
 */
export default function AppPasswordTutorial({ open, onClose, provider = "gmail" }) {
  const [step, setStep] = useState(1);

  React.useEffect(() => { if (open) setStep(1); }, [open]);

  const isOutlook = provider === "outlook";
  const meta = isOutlook
    ? {
        brand: "Outlook",
        appPwdUrl: "https://account.live.com/proofs/AppPassword",
        twoStepUrl: "https://account.live.com/proofs/Manage",
      }
    : {
        brand: "Gmail",
        appPwdUrl: "https://myaccount.google.com/apppasswords",
        twoStepUrl: "https://myaccount.google.com/signinoptions/two-step-verification",
      };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="bg-slate-900 border-white/10 max-w-lg z-[200]"
        data-testid="apppwd-tutorial-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <KeyRound className="text-amber-300" size={18} />
            Genera la teva App Password de {meta.brand} en 60 segons
          </DialogTitle>
        </DialogHeader>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-3 py-2"
             data-testid="apppwd-progress">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-3">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition ${
                  s < step ? "bg-emerald-500/80 text-white" :
                  s === step ? "bg-amber-500 text-white ring-4 ring-amber-500/20" :
                  "bg-white/10 text-slate-400"
                }`}
              >
                {s < step ? <CheckCircle2 size={14} /> : s}
              </div>
              {s < 3 && (
                <div className={`w-12 h-0.5 ${s < step ? "bg-emerald-500/80" : "bg-white/10"}`} />
              )}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-3" data-testid="apppwd-step-1">
            <h4 className="text-base font-semibold text-white flex items-center gap-2">
              <ShieldCheck size={16} className="text-blue-300" />
              Pas 1 · Activa la Verificació en 2 passos
            </h4>
            <p className="text-sm text-slate-300">
              Google només permet crear contrasenyes d'aplicació si el teu compte
              té la verificació en 2 passos activada. Si ja la tens, passa al pas següent.
            </p>
            {/* Visual mockup */}
            <MockPanel
              titleIcon={<ShieldCheck size={14} />}
              title="Seguretat del compte"
              rows={[
                { label: "Verificació en 2 passos", value: "ACTIVAR", highlight: true },
                { label: "Contrasenyes d'aplicació", value: "Bloquejat" },
              ]}
            />
            <div className="flex items-center justify-between pt-2">
              <button type="button" onClick={onClose}
                      className="text-xs text-slate-500 hover:text-slate-300"
                      data-testid="apppwd-cancel-1">
                Cancel·lar
              </button>
              <div className="flex items-center gap-2">
                <a href={meta.twoStepUrl} target="_blank" rel="noreferrer"
                   className="text-xs px-3 py-1.5 rounded-md border border-blue-400/30 bg-blue-500/10 text-blue-200 hover:bg-blue-500/20 inline-flex items-center gap-1.5"
                   data-testid="apppwd-link-2step">
                  Anar a 2 passos <ExternalLink size={11} />
                </a>
                <Button onClick={() => setStep(2)}
                        data-testid="apppwd-next-1"
                        className="bg-blue-600 hover:bg-blue-700">
                  Ja la tinc <ArrowRight size={13} className="ml-1.5" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="apppwd-step-2">
            <h4 className="text-base font-semibold text-white flex items-center gap-2">
              <KeyRound size={16} className="text-amber-300" />
              Pas 2 · Crea la contrasenya
            </h4>
            <p className="text-sm text-slate-300">
              Visita la pàgina de contrasenyes d'aplicació, dóna-li un nom
              identificable (per exemple <strong>HOSYS Factures</strong>) i fes
              clic a <strong>Crear</strong>.
            </p>
            <MockPanel
              titleIcon={<KeyRound size={14} />}
              title="Contrasenyes d'aplicació"
              rows={[
                { label: "Nom de l'app", value: "HOSYS Factures", input: true },
              ]}
              footer={
                <div className="flex items-center gap-1 text-xs text-amber-200 font-semibold">
                  <MousePointerClick size={12} className="animate-pulse" />
                  Clic a "Crear"
                </div>
              }
            />
            <div className="flex items-center justify-between pt-2">
              <button type="button" onClick={() => setStep(1)}
                      className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1">
                <ArrowLeft size={11} /> Enrere
              </button>
              <div className="flex items-center gap-2">
                <a href={meta.appPwdUrl} target="_blank" rel="noreferrer"
                   className="text-xs px-3 py-1.5 rounded-md border border-amber-400/30 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 inline-flex items-center gap-1.5"
                   data-testid="apppwd-link-create">
                  Crear contrasenya <ExternalLink size={11} />
                </a>
                <Button onClick={() => setStep(3)}
                        data-testid="apppwd-next-2"
                        className="bg-amber-600 hover:bg-amber-700">
                  Ja la he creat <ArrowRight size={13} className="ml-1.5" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3" data-testid="apppwd-step-3">
            <h4 className="text-base font-semibold text-white flex items-center gap-2">
              <Copy size={16} className="text-emerald-300" />
              Pas 3 · Copia i enganxa
            </h4>
            <p className="text-sm text-slate-300">
              Google et mostrarà una contrasenya de 16 caràcters dividida en 4 blocs.
              <strong className="text-amber-200"> Copia-la sencera</strong> (amb o sense espais, és igual)
              i enganxa-la al camp <strong>Contrasenya</strong> de l'app HOSYS.
            </p>
            {/* Visual mockup of Google's password reveal */}
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.04] p-4 text-center">
              <div className="text-[10px] uppercase tracking-wider text-emerald-200 mb-2">
                Contrasenya generada (exemple)
              </div>
              <div className="font-mono text-lg text-white tracking-wider">
                <span className="px-1.5 py-0.5 rounded bg-white/10">abcd</span>{" "}
                <span className="px-1.5 py-0.5 rounded bg-white/10">efgh</span>{" "}
                <span className="px-1.5 py-0.5 rounded bg-white/10">ijkl</span>{" "}
                <span className="px-1.5 py-0.5 rounded bg-white/10">mnop</span>
              </div>
              <div className="text-[10px] text-emerald-300/70 mt-2 flex items-center justify-center gap-1">
                <Copy size={10} /> Botó "Copiar" → enganxa a HOSYS
              </div>
            </div>
            <div className="rounded-lg border border-blue-500/30 bg-blue-500/[0.04] p-3 text-xs text-blue-100 flex items-start gap-2">
              <Mail size={13} className="flex-shrink-0 mt-0.5" />
              <div>
                <strong>Important:</strong> aquesta contrasenya només es mostra UNA vegada.
                Si la perds, simplement torna a la pàgina d'App Passwords i revoca l'antiga + crea'n una de nova.
              </div>
            </div>
            <div className="flex items-center justify-between pt-2">
              <button type="button" onClick={() => setStep(2)}
                      className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1">
                <ArrowLeft size={11} /> Enrere
              </button>
              <Button onClick={onClose}
                      data-testid="apppwd-finish"
                      className="bg-emerald-600 hover:bg-emerald-700">
                <CheckCircle2 size={13} className="mr-1.5" />
                Llest, vull enganxar-la
              </Button>
            </div>
          </div>
        )}

        <button type="button" onClick={onClose}
                aria-label="close"
                className="absolute top-3 right-3 text-slate-500 hover:text-slate-300"
                data-testid="apppwd-close">
          <X size={16} />
        </button>
      </DialogContent>
    </Dialog>
  );
}

/** Inline "Google-like" panel mockup. Pure CSS so no external assets. */
function MockPanel({ titleIcon, title, rows, footer }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] overflow-hidden">
      <div className="px-3 py-2 border-b border-white/5 text-xs text-slate-300 font-semibold flex items-center gap-2">
        {titleIcon}{title}
      </div>
      <div className="divide-y divide-white/5">
        {rows.map((r, i) => (
          <div key={i} className="px-3 py-2.5 flex items-center justify-between text-xs">
            <span className="text-slate-300">{r.label}</span>
            {r.input ? (
              <span className="font-mono text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-0.5">
                {r.value}
              </span>
            ) : (
              <span className={r.highlight
                ? "text-blue-300 font-semibold underline cursor-pointer"
                : "text-slate-500"}>
                {r.value}
              </span>
            )}
          </div>
        ))}
      </div>
      {footer && (
        <div className="px-3 py-2 border-t border-white/5 bg-white/[0.02]">
          {footer}
        </div>
      )}
    </div>
  );
}
